// External Intake & Collection v1.0 PR 2 — System-Admin list page for
// CollectionTemplate. Gated by Collection.ManageTemplates. Routes:
//   /admin/collection-templates           → list (this page)
//   /admin/collection-templates/new       → create
//   /admin/collection-templates/:id       → edit

import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, Plus } from "lucide-react";
import { Badge, Button } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { PageHeader } from "@/components/layout/PageHeader";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { useToast } from "@/components/ui/Toast";
import {
  COLLECTION_AUDIENCES,
  collectionAudienceLabel,
  collectionTemplatesApi,
  formatAudiences,
  type CollectionTemplateDto,
  type MaterialAudience,
} from "@/api/collectionTemplates";
import { TemplateKindBadge } from "@/components/Collection/TemplateKindBadge";
import { invalidateCollectionTemplateReads } from "./CollectionTemplateEditPage";

const KIND_TONE: Record<CollectionTemplateDto["kind"], string> = {
  Form: "Form",
  Upload: "Upload",
  ESign: "E-Sign",
  Education: "Education",
};

export function CollectionTemplatesPage() {
  // Round-3 #7 — External Collection › Collection Templates.
  useBreadcrumbs([
    { label: "External Collection" },
    { label: "Collection Templates" },
  ]);
  const qc = useQueryClient();
  const { toast } = useToast();
  const [showInactive, setShowInactive] = useState(false);
  const [audienceFilter, setAudienceFilter] = useState<MaterialAudience | "">("");

  const list = useQuery({
    queryKey: ["collection-templates-admin", showInactive, audienceFilter || null],
    queryFn: () =>
      collectionTemplatesApi.list(showInactive, audienceFilter || null),
  });

  const deactivate = useMutation({
    mutationFn: (id: string) => collectionTemplatesApi.deactivate(id),
    onSuccess: () => {
      toast({ tone: "success", message: "Template deactivated." });
      invalidateCollectionTemplateReads(qc);
    },
    onError: (e: Error) => toast({ tone: "error", message: e.message }),
  });

  const rows = list.data ?? [];

  const columns: Column<CollectionTemplateDto>[] = [
    {
      key: "name",
      header: "Template",
      render: (t) => (
        <Link
          to={`/admin/collection-templates/${t.id}`}
          className="font-semibold text-onyx hover:text-gold-shadow"
        >
          <div className="flex items-center gap-space-2">
            <span>{t.name}</span>
            {!t.active && <Badge status="archived">Inactive</Badge>}
            <Badge status="info">v{t.version}</Badge>
          </div>
          {t.description && (
            <div className="text-caption text-gray-mid font-normal max-w-md truncate">
              {t.description}
            </div>
          )}
        </Link>
      ),
    },
    {
      key: "kind",
      header: "Kind",
      render: (t) => (
        <TemplateKindBadge kind={KIND_TONE[t.kind]} isSpecialForm={t.isSpecialForm} />
      ),
    },
    {
      key: "audience",
      header: "Audience",
      // Collection Templates — Audience Multiselect. Render the set; truncate to
      // the first two labels + a "+N" overflow with the full list on hover (§7).
      render: (t) => {
        const { text, title } = formatAudiences(t.audiences, 2);
        return (
          <span className="text-small text-onyx" title={title}>
            {text}
          </span>
        );
      },
    },
    {
      key: "tag",
      header: "Document tag",
      render: (t) =>
        t.documentTagCode ? (
          <span className="text-small text-onyx">{t.documentTagCode}</span>
        ) : (
          <span className="text-caption text-gray-mid italic">—</span>
        ),
    },
    {
      key: "signature",
      header: "Signature",
      render: (t) =>
        t.requiresSignature ? (
          <Badge status="info">{t.signaturePath ?? "Required"}</Badge>
        ) : (
          <span className="text-caption text-gray-mid italic">—</span>
        ),
    },
    {
      key: "actions",
      header: "",
      render: (t) =>
        t.active ? (
          <Button
            variant="secondary"
            size="sm"
            type="button"
            onClick={() => {
              if (confirm(`Deactivate '${t.name}'? In-flight items keep their snapshot.`)) {
                deactivate.mutate(t.id);
              }
            }}
          >
            <Archive size={14} className="mr-space-1" /> Deactivate
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="flex flex-col gap-space-4">
      <PageHeader
        eyebrow="Admin · Collection"
        title="Collection templates"
        description="Requestable Form, Upload, and E-Sign items."
        actions={
          <Link to="/admin/collection-templates/new">
            <Button type="button">
              <Plus size={16} className="mr-space-1" /> New template
            </Button>
          </Link>
        }
      />

      <div className="flex items-end gap-space-3">
        <div>
          <label
            htmlFor="ct-audience"
            className="font-display text-label font-semibold uppercase tracking-label text-gray-mid block mb-space-1"
          >
            Audience
          </label>
          <Select
            id="ct-audience"
            value={audienceFilter}
            onChange={(e) =>
              setAudienceFilter(e.target.value as MaterialAudience | "")
            }
            className="w-48"
          >
            <option value="">All audiences</option>
            {COLLECTION_AUDIENCES.map((a) => (
              <option key={a} value={a}>
                {collectionAudienceLabel(a)}
              </option>
            ))}
          </Select>
        </div>
        <label className="flex items-center gap-space-2 text-small text-onyx pb-space-2">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Show inactive versions
        </label>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(t) => t.id}
        loading={list.isLoading}
        error={list.error instanceof Error ? list.error.message : null}
        emptyMessage="No templates yet. Click 'New template' to author one."
      />
    </div>
  );
}
